import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Button,
  SimpleGrid,
  Text,
  VStack,
  Flex,
  Tooltip,
} from '@chakra-ui/react'
import { BookOpen, Brain, ClipboardList, FileText, GraduationCap, LucideIcon, MessageSquareOff, ScrollText, Target } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ContentType } from '../../types/content/content'
import { getContentPickerGroups } from '../../pages/user/Content/contentEditorConfig'

interface ContentTypePickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectType: (type: ContentType) => void
}

const groupTitles = {
  learning: 'content.modals.createOptions.groups.learning',
  survey: 'content.modals.createOptions.groups.survey',
  content: 'content.modals.createOptions.groups.content',
}

const TYPE_ICONS: Record<ContentType, LucideIcon> = {
  [ContentType.Training]: GraduationCap,
  [ContentType.Learning]: BookOpen,
  [ContentType.Survey]: ClipboardList,
  [ContentType.AnonymousSurvey]: MessageSquareOff,
  [ContentType.Skills]: Brain,
  [ContentType.Competence]: Target,
  [ContentType.Task]: FileText,
  [ContentType.Goal]: Target,
  [ContentType.Note]: ScrollText,
  [ContentType.Form]: ClipboardList,
}

const TYPE_COLORS: Record<ContentType, { bg: string; icon: string; border: string }> = {
  [ContentType.Training]: { bg: '#DBEAFE', icon: '#1D4ED8', border: '#93C5FD' },
  [ContentType.Learning]: { bg: '#ECFDF5', icon: '#059669', border: '#A7F3D0' },
  [ContentType.Survey]: { bg: '#FFF7ED', icon: '#EA580C', border: '#FED7AA' },
  [ContentType.AnonymousSurvey]: { bg: '#EEF2FF', icon: '#4F46E5', border: '#C7D2FE' },
  [ContentType.Skills]: { bg: '#EDE9FE', icon: '#6D28D9', border: '#C4B5FD' },
  [ContentType.Competence]: { bg: '#F3F4F6', icon: '#374151', border: '#D1D5DB' },
  [ContentType.Task]: { bg: '#FEF3C7', icon: '#B45309', border: '#FCD34D' },
  [ContentType.Goal]: { bg: '#E0F2FE', icon: '#0369A1', border: '#7DD3FC' },
  [ContentType.Note]: { bg: '#FCE7F3', icon: '#BE185D', border: '#F9A8D4' },
  [ContentType.Form]: { bg: '#F5F5F4', icon: '#57534E', border: '#D6D3D1' },
}

const DISPLAY_LABELS: Partial<Record<ContentType, string>> = {
  [ContentType.Training]: 'Create your own e-learning',
  [ContentType.Learning]: 'E-learning with URL',
}

const ContentTypePickerModal: React.FC<ContentTypePickerModalProps> = ({
  isOpen,
  onClose,
  onSelectType,
}) => {
  const { t } = useTranslation()
  const groups = getContentPickerGroups()

  const renderCard = (type: ContentType, label: string, description: string) => {
    const Icon = TYPE_ICONS[type]
    const colors = TYPE_COLORS[type]
    const displayLabel =
      type === ContentType.Training
        ? t('content.modals.createOptions.options.createTraining')
        : type === ContentType.Learning
        ? t('content.modals.createOptions.options.createLearningUrl')
        : t(`content.types.${type}`, label)

    return (
      <Box
        key={type}
        as="button"
        p="16px"
        borderRadius="14px"
        border="1.5px solid"
        borderColor="solid.50"
        bg="white"
        cursor="pointer"
        transition="all 0.18s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          borderColor: colors.border,
          bg: colors.bg,
          transform: 'translateY(-2px)',
          boxShadow: `0px 4px 12px ${colors.icon}14`,
        }}
        _active={{
          transform: 'translateY(0px) scale(0.98)',
        }}
        onClick={() => {
          onSelectType(type)
          onClose()
        }}
        textAlign="left"
        display="flex"
        flexDirection="column"
        gap="10px"
      >
        <Flex
          w="40px"
          h="40px"
          borderRadius="11px"
          bg={colors.bg}
          align="center"
          justify="center"
          border="1px solid"
          borderColor={colors.border}
          transition="transform 0.18s ease"
          sx={{
            'button:hover &': { transform: 'scale(1.08)' },
          }}
        >
          <Icon size={20} color={colors.icon} />
        </Flex>
        <Tooltip label={displayLabel} placement="top" hasArrow openDelay={300}>
          <VStack align="flex-start" spacing="4px">
            <Text fontSize="13px" fontWeight="700" color="solid.800" lineHeight="1.2" noOfLines={2}>
              {displayLabel}
            </Text>
            <Text fontSize="12px" color="solid.500" whiteSpace="normal" noOfLines={3}>
              {description}
            </Text>
          </VStack>
        </Tooltip>
      </Box>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="16px" boxShadow="0 8px 32px rgba(0,0,0,0.12)">
        <ModalHeader pt="28px" pb="12px">
          <Text textAlign="center" fontSize="20px" fontWeight="700" color="solid.900">
            {t('content.modals.createOptions.title')}
          </Text>
          <Text textAlign="center" fontSize="13px" color="solid.500" mt="6px">
            {t('content.modals.createOptions.description')}
          </Text>
        </ModalHeader>
        <ModalCloseButton
          top="20px"
          right="20px"
          sx={{
            border: 'none',
            bg: 'transparent',
            _hover: { bg: 'solid.25', borderRadius: '6px' },
            _focus: { outline: 'none', boxShadow: 'none', border: 'none' },
          }}
        />
        <ModalBody px="32px" pb="32px">
          {(['learning', 'survey', 'content'] as const).map((groupKey) => (
            <Box key={groupKey} mb={groupKey === 'content' ? 0 : 6}>
              <Text fontSize="13px" fontWeight="700" color="solid.500" mb="12px" textTransform="uppercase" letterSpacing="0.08em">
                {t(groupTitles[groupKey])}
              </Text>
              <SimpleGrid columns={{ base: 1, md: groupKey === 'content' ? 3 : 2 }} spacing="8px">
                {groups[groupKey].map((option) =>
                  renderCard(
                    option.type,
                    option.label,
                    t(`content.editor.typeDescriptions.${option.type}`, option.description),
                  ),
                )}
              </SimpleGrid>
            </Box>
          ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default ContentTypePickerModal
